"use client";

import React, { useRef, useState, useEffect, useCallback } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { getBaseImageUrl, getMerchantHref, getRandomCategorySeoTitle } from '@/constants/hooks';
import { CompanyWiseMerchant, Merchant } from '@/services/dataTypes';

interface Props {
    merchants: CompanyWiseMerchant[] | Merchant[];
    categoryName: string;
    storeSlug: string;
    slugType: string;
    domain: string;
}

const MerchantSlider = ({ merchants, categoryName, storeSlug, slugType, domain }: Props) => {
    const trackRef = useRef<HTMLDivElement>(null);
    const [canPrev, setCanPrev] = useState(false);
    const [canNext, setCanNext] = useState(true);

    const syncState = useCallback(() => {
        const t = trackRef.current;
        if (!t) return;
        setCanPrev(t.scrollLeft > 4);
        setCanNext(t.scrollLeft < t.scrollWidth - t.clientWidth - 4);
    }, []);

    useEffect(() => {
        const t = trackRef.current;
        if (!t) return;
        syncState();
        t.addEventListener('scroll', syncState, { passive: true });
        window.addEventListener('resize', syncState);
        return () => {
            t.removeEventListener('scroll', syncState);
            window.removeEventListener('resize', syncState);
        };
    }, [merchants, syncState]);

    const scroll = (dir: 'left' | 'right') => {
        trackRef.current?.scrollBy({ left: dir === 'left' ? -300 : 300, behavior: 'smooth' });
    };

    if (!merchants?.length) return null;

    return (
        <section className="py-10 bg-white" style={{ borderBottom: '1px solid #E8F5EE' }}>
            <div className="container mx-auto px-4 md:px-8 max-w-7xl">

                {/* ── Header ── */}
                <div className="flex items-center justify-between gap-4 mb-8">
                    <div className="flex items-center gap-3">
                        <div className="w-1 h-6 rounded-full bg-[#5D27A1]" />
                        <div>
                            <p className="text-[10px] font-semibold uppercase tracking-widest m-0 mb-0.5"
                                style={{ color: '#94A3B8' }}>
                                In This Category
                            </p>
                            <h2 className="text-xl font-bold m-0" style={{ color: '#1E293B' }}>
                                Trending Merchants in{' '}
                                <span style={{ color: '#5D27A1' }}>
                                    {getRandomCategorySeoTitle(categoryName)}
                                </span>
                            </h2>
                        </div>
                    </div>

                    {/* Count badge + nav buttons */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <span
                            className="hidden sm:inline-flex items-center px-3 py-1.5 rounded-lg text-[11px] font-semibold mr-2"
                            style={{ background: '#E8F5EE', color: '#5D27A1' }}
                        >
                            {merchants.length} stores
                        </span>

                        {/* Prev */}
                        <button
                            onClick={() => scroll('left')}
                            disabled={!canPrev}
                            aria-label="Scroll left"
                            className="w-8 h-8 rounded-xl flex items-center justify-center transition-all duration-200 disabled:opacity-30 disabled:pointer-events-none hover:bg-[#E8F5EE]"
                            style={{ border: '1px solid #E8F5EE', color: '#5D27A1' }}
                        >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>

                        {/* Next */}
                        <button
                            onClick={() => scroll('right')}
                            disabled={!canNext}
                            aria-label="Scroll right"
                            className="w-8 h-8 rounded-xl flex items-center justify-center transition-all duration-200 disabled:opacity-30 disabled:pointer-events-none hover:bg-[#E8F5EE]"
                            style={{ border: '1px solid #E8F5EE', color: '#5D27A1' }}
                        >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* ── Slider track ── */}
                <div className="relative">
                    {/* Left fade */}
                    <div
                        className="absolute left-0 top-0 bottom-0 w-12 z-10 pointer-events-none transition-opacity duration-300"
                        style={{
                            background: 'linear-gradient(90deg, #FFFFFF, transparent)',
                            opacity: canPrev ? 1 : 0,
                        }}
                    />
                    {/* Right fade */}
                    <div
                        className="absolute right-0 top-0 bottom-0 w-12 z-10 pointer-events-none transition-opacity duration-300"
                        style={{
                            background: 'linear-gradient(270deg, #FFFFFF, transparent)',
                            opacity: canNext ? 1 : 0,
                        }}
                    />

                    <div
                        ref={trackRef}
                        className="flex gap-3 overflow-x-auto py-2"
                        style={{
                            scrollbarWidth: 'none',
                            msOverflowStyle: 'none',
                            scrollSnapType: 'x mandatory',
                        }}
                    >
                        {merchants.map((merchant, i) => {
                            const href = getMerchantHref(merchant, storeSlug, slugType);
                            const logo = getBaseImageUrl(domain, merchant?.merchant_logo, '');
                            const initials = merchant?.merchant_name?.slice(0, 2).toUpperCase() || '??';

                            return (
                                <Link
                                    key={merchant.unique_id ?? i}
                                    href={href}
                                    className="group relative flex flex-col items-center justify-between gap-3 p-4 bg-white rounded-2xl no-underline transition-all duration-300 hover:-translate-y-1 hover:shadow-lg flex-shrink-0 overflow-hidden"
                                    style={{
                                        width: '130px',
                                        border: '1px solid #E8F5EE',
                                        boxShadow: '0 2px 8px rgba(74,154,110,0.04)',
                                        scrollSnapAlign: 'start',
                                    }}
                                >
                                    {/* Subtle hover gradient */}
                                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                                        style={{ background: 'linear-gradient(135deg, rgba(74,154,110,0.03) 0%, transparent 60%)' }} />

                                    {/* Top accent line on hover */}
                                    <div className="absolute top-0 left-0 right-0 h-[2px] rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                                        style={{ background: 'linear-gradient(90deg, transparent, #5D27A1, transparent)' }} />

                                    {/* Logo */}
                                    <div
                                        className="relative w-14 h-14 rounded-xl bg-white overflow-hidden flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-105"
                                        style={{ border: '1px solid #F1F5F9', boxShadow: '0 2px 6px rgba(0,0,0,0.04)' }}
                                    >
                                        {merchant?.merchant_logo ? (
                                            <Image
                                                src={logo}
                                                alt={merchant?.merchant_name || ''}
                                                fill unoptimized
                                                className="object-contain p-2"
                                                sizes="56px"
                                            />
                                        ) : (
                                            <span className="text-sm font-bold transition-colors duration-300 group-hover:text-white"
                                                style={{ color: '#5D27A1' }}>
                                                {initials}
                                            </span>
                                        )}
                                    </div>

                                    {/* Name */}
                                    <span
                                        className="relative z-10 text-[11px] font-semibold text-center leading-tight line-clamp-2 w-full transition-colors duration-200 group-hover:text-[#5D27A1]"
                                        style={{ color: '#1E293B' }}
                                    >
                                        {merchant?.merchant_name}
                                    </span>

                                    {/* Green accent bar */}
                                    <div className="h-[2px] w-8 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-[#5D27A1]" />
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default MerchantSlider;
